// src/pages/admin/AdminEventForm.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import AdminLayout from "./AdminLayout";

import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

import { toast } from "sonner";
import { createAdminEvent, getAdminEvent, updateAdminEvent } from "../../lib/adminApi";

const STATUS_OPTIONS = [
  { value: "upcoming", label: "Upcoming" },
  { value: "ongoing", label: "Ongoing" },
  { value: "completed", label: "Completed" },
];

const CATEGORY_OPTIONS = [
  { value: "outreach", label: "Outreach" },
  { value: "campaign", label: "Campaign" },
  { value: "training", label: "Training" },
  { value: "advocacy", label: "Advocacy" },
  { value: "community_visit", label: "Community Visit" },
];

const emptyForm = {
  title: "",
  venue: "",
  date: "",
  end_date: "",

  start_time: "",
  end_time: "",

  category: "outreach",
  status: "upcoming",
  is_virtual: "false",

  description: "",

  objectives_text: "",
  target_beneficiaries: "",

  lives_touched: 0,
  materials_shared: 0,

  phone_number: "",
  organizer: "GLGIF",
  enquiries_email: "info@glgif.org",
};

export default function AdminEventForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const title = useMemo(() => (isEdit ? "Edit Event" : "Create Event"), [isEdit]);

  useEffect(() => {
    const load = async () => {
      if (!isEdit) return;
      try {
        const data = await getAdminEvent(id);

        setForm((prev) => ({
          ...prev,
          title: data?.title || "",
          venue: data?.venue || data?.location || "",
          date: data?.date || "",
          end_date: data?.end_date || "",

          start_time: data?.start_time || data?.time || "",
          end_time: data?.end_time || "",

          category: data?.category || "outreach",
          status: data?.status || "upcoming",
          is_virtual: String(Boolean(data?.is_virtual)),

          description: data?.description || "",

          objectives_text: Array.isArray(data?.objectives) ? data.objectives.join(", ") : "",
          target_beneficiaries: data?.target_beneficiaries || "",

          lives_touched: Number(data?.lives_touched || 0),
          materials_shared: Number(data?.materials_shared || 0),

          phone_number: data?.phone_number || data?.coordinator_contact || "",
          organizer: data?.organizer || data?.organiser || "GLGIF",
          enquiries_email: data?.enquiries_email || "info@glgif.org",
        }));
      } catch (err) {
        toast.error(err?.message || "Failed to load event");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEdit]);

  const onChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!isEdit && !image) {
      toast.error("Event image is required");
      return;
    }

    const objectives = String(form.objectives_text || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("venue", form.venue);

    fd.append("date", form.date);
    if (form.end_date) fd.append("end_date", form.end_date);

    fd.append("start_time", form.start_time);
    fd.append("end_time", form.end_time);

    fd.append("category", form.category);
    fd.append("status", form.status);
    fd.append("is_virtual", form.is_virtual === "true" ? "true" : "false");

    fd.append("description", form.description);

    fd.append("objectives_json", JSON.stringify(objectives));
    fd.append("target_beneficiaries", form.target_beneficiaries || "");

    fd.append("lives_touched", String(Number(form.lives_touched || 0)));
    fd.append("materials_shared", String(Number(form.materials_shared || 0)));

    fd.append("phone_number", form.phone_number);
    fd.append("organizer", form.organizer);
    fd.append("enquiries_email", form.enquiries_email);

    if (image) fd.append("image", image);

    setSaving(true);
    try {
      if (isEdit) {
        await updateAdminEvent(id, fd);
        toast.success("Event updated successfully");
      } else {
        await createAdminEvent(fd);
        toast.success("Event created successfully");
      }
      navigate("/admin/events");
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={title}>
        <div className="h-40 rounded-2xl bg-stone-200 animate-pulse" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={title}>
      <Card className="rounded-2xl border-stone-200">
        <CardContent className="p-6">
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => onChange("title", e.target.value)} required />
              </div>

              <div>
                <Label>Venue *</Label>
                <Input value={form.venue} onChange={(e) => onChange("venue", e.target.value)} required />
              </div>

              <div>
                <Label>Start Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => onChange("date", e.target.value)} required />
              </div>

              <div>
                <Label>End Date (Optional)</Label>
                <Input type="date" value={form.end_date} onChange={(e) => onChange("end_date", e.target.value)} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start Time *</Label>
                  <Input type="time" value={form.start_time} onChange={(e) => onChange("start_time", e.target.value)} required />
                </div>
                <div>
                  <Label>End Time *</Label>
                  <Input type="time" value={form.end_time} onChange={(e) => onChange("end_time", e.target.value)} required />
                </div>
              </div>

              <div>
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={(v) => onChange("category", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status *</Label>
                <Select value={form.status} onValueChange={(v) => onChange("status", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-stone-500">
                  Note: backend may auto-correct status based on date/time (past events become completed).
                </p>
              </div>

              <div>
                <Label>Virtual Event?</Label>
                <Select value={form.is_virtual} onValueChange={(v) => onChange("is_virtual", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="false">No</SelectItem>
                    <SelectItem value="true">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Phone Number *</Label>
                <Input value={form.phone_number} onChange={(e) => onChange("phone_number", e.target.value)} required />
              </div>

              <div>
                <Label>Organizer (Fullname of GLGIF) *</Label>
                <Input value={form.organizer} onChange={(e) => onChange("organizer", e.target.value)} required />
              </div>

              <div>
                <Label>Enquiries Contact Email *</Label>
                <Input
                  type="email"
                  value={form.enquiries_email}
                  onChange={(e) => onChange("enquiries_email", e.target.value)}
                  required
                />
              </div>

              <div>
                <Label>{isEdit ? "Replace Image (Optional)" : "Image *"}</Label>
                <Input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setImage(e.target.files?.[0] || null)} />
              </div>
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                rows={6}
                value={form.description}
                onChange={(e) => onChange("description", e.target.value)}
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Objectives (comma-separated)</Label>
                <Input
                  value={form.objectives_text}
                  onChange={(e) => onChange("objectives_text", e.target.value)}
                  placeholder="E.g. Provide relief, Raise awareness, Mobilize volunteers"
                />
              </div>

              <div>
                <Label>Target Beneficiaries</Label>
                <Input
                  value={form.target_beneficiaries}
                  onChange={(e) => onChange("target_beneficiaries", e.target.value)}
                  placeholder="E.g. Vulnerable children, widows, displaced households"
                />
              </div>

              <div>
                <Label>Lives Touched (completed events)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.lives_touched}
                  onChange={(e) => onChange("lives_touched", e.target.value)}
                />
              </div>

              <div>
                <Label>Materials Shared (completed events)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.materials_shared}
                  onChange={(e) => onChange("materials_shared", e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button type="submit" disabled={saving} className="bg-amber-700 hover:bg-amber-800 rounded-xl">
                {saving ? "Saving..." : isEdit ? "Update Event" : "Create Event"}
              </Button>
              <Button type="button" variant="outline" asChild className="rounded-xl">
                <Link to="/admin/events">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}